import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Modal,
  Typography,
} from "@mui/material";
import { TOrder$TikiRental, TOrdersList, useAPI } from "../api.js";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { dateTimeFormatter } from "./DoorCardUtils.js";

export const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  maxHeight: "60vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

export const headerStyle = {
  p: 2,
  borderBottom: "1px solid #ccc",
  backgroundColor: "#f5f5f5",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

export const bodyStyle = {
  p: 2,
  overflowY: "auto",
  flexGrow: 1,
};

export const rentalStyle = {
  width: "100%",
  borderRadius: "10px",
  backgroundColor: "#b7b8b8",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  marginBottom: "1rem",
};

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: "10px",
}));

const RowCell = styled(Box)((theme) => ({
  display: "flex",
  gap: "10px",
  padding: "8px",
  height: "100%",
  alignItems: "center",
}));

export const NoOverflowTypography = styled(Typography)(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  minWidth: "0",
}));

export const NoResultsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

interface RentalListModalProps {
  tenantKey: string;
  locationID: string;
  doorID: string;
  openStatus: boolean;
  onClose: () => void;
  rentalsHistory: TOrder$TikiRental[] | [];
  ordersHistory: TOrdersList[] | { error: string } | [];
}

export const RentalListModal = (props: RentalListModalProps) => {
  const { t } = useTranslation();
  const api = useAPI();

  function rentalOrderId(rental: TOrder$TikiRental) {
    let userName = "";
    if (
      props.ordersHistory instanceof Array &&
      props.ordersHistory.length > 0
    ) {
      const rentalOrder = props.ordersHistory.filter(
        (od) => od.order.raw.orderID == rental.orderID,
      );

      userName = rentalOrder[0]?.order?.raw?.customer?.username;
    }

    return userName;
  }

  const handleClose = () => {
    props.onClose();
  };

  const formattedDateTime = (rental: TOrder$TikiRental): string => {
    if (!rental) {
      return "";
    }

    return dateTimeFormatter(
      Date.parse(rental.createdAt),
      Date.parse(rental.expiredAt),
    );
  };

  const doorRentals = (
    rentalList: TOrder$TikiRental[],
  ): TOrder$TikiRental[] => {
    return rentalList.filter((item) =>
      item.rentalItems.some((rItem) =>
        rItem?.resources.some((r) => r.doorID === props.doorID),
      ),
    );
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.openStatus}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={props.openStatus}>
        <Box sx={modalStyle}>
          <Box sx={headerStyle}>
            <Typography id="transition-modal-title" variant="h6" component="h2">
              {t("location_details.rental_list_title")}
            </Typography>
          </Box>
          <Box id="transition-modal-description" sx={bodyStyle}>
            {doorRentals(props.rentalsHistory).length > 0 &&
              doorRentals(props.rentalsHistory)?.map((rt) => {
                return (
                  <Box sx={rentalStyle} key="">
                    <Row>
                      <RowCell sx={{ width: "40%" }}>
                        <NoOverflowTypography>
                          <Link
                            to={`/tenants/${props.tenantKey}/orders/${rt.orderID}`}
                          >
                            {rt.orderID}
                          </Link>
                        </NoOverflowTypography>
                      </RowCell>
                      <RowCell sx={{ width: "30%" }}>
                        <NoOverflowTypography>
                          {rentalOrderId(rt)}
                        </NoOverflowTypography>
                      </RowCell>
                      <RowCell sx={{ width: "30%" }}>
                        <NoOverflowTypography>
                          {formattedDateTime(rt)}
                        </NoOverflowTypography>
                      </RowCell>
                    </Row>
                  </Box>
                );
              })}
            {doorRentals(props.rentalsHistory).length == 0 && (
              <NoResultsContainer>
                <Typography variant="subtitle1" color="error">
                  {t("location_details.no_previous_rentals")}
                </Typography>
              </NoResultsContainer>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
